import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// @ts-ignore
import { Card, Button } from 'dsm-react-lib'
import {
  IconDotsVertical,
  IconEye,
  IconMessageCircle,
  IconRobotFace,
  IconSquarePlus
} from '@tabler/icons-react'
import { MonitorIcon } from '../Chatbot/icons/Icons'
import Skeleton from 'react-loading-skeleton'
import './styles.css'
import AgentCards from './AgentCards'
import { AgentData } from '../Chatbot/utils/types'
import EmptyContent from './EmptyContent'
import { ScrollContainer, useCustomScrollbar } from '../../../components/ScrollContainer'
import { useSelector } from 'react-redux'
import { Role } from '../../../utility/types'

export interface MostUsedAgentProps {
  agentData: AgentData[]
  isLoading?: boolean
  role?: string
  isPublicTeam?: boolean
  setActiveTab?: (tab: number) => void
  setAgentsData?: (data: AgentData[]) => void
  // May return an enriched agent (or a Promise resolving to one) to use in place of the
  // agent passed via `agentData` when dispatching/navigating to chat.
  onBeforeNavigate?: () => any
}

export const renderAgentSkeletonLoader = () => {
  return (
    <div className="flex mt-2 pt-12 !gap-[1.5rem]">
      {[...Array(3)].map((_: any, index: number) => (
        <div key={index} className="agent-card-wrapper my-1 mr-1">
          <Card className="h-full min-w-[350px] min-h-[200px] flex-shrink-0 cursor-pointer !shadow-none transition-all duration-300 hover:scale-105 relative overflow-visible z-[1] mb-0 ">
            <div
              className={`flex flex-col items-center theme-card-border rounded-lg w-[360px] min-h-[100%] overflow-visible relative transition-shadow`}>
              <div
                className={`rounded-full w-24 h-24 px-2 py-1 pointer-events-none absolute left-[1.5rem] -top-[3rem] z-[2] flex items-center justify-center`}>
                <div className="min-w-[100px] h-[100px] rounded-full overflow-hidden flex items-center justify-center">
                  <Skeleton circle={true} width={85} height={85} />
                </div>
              </div>
              <div className="mt-4 p-2 relative">
                <Skeleton className="animated-skeleton mb-2" height={25} width={200} />
                <Skeleton className="animated-skeleton" count={2} width={300} />
                <div className="absolute -top-6 right-4 flex items-center space-x-1">
                  {/* <MonitorIcon size={15} /> */}
                  <IconDotsVertical size={15} />
                </div>
              </div>
              <div className="theme-surface-muted flex flex-col p-7 w-full h-full rounded-b-lg min-h-[calc(100%-200px)]">
                <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                  <div className="flex flex-col items-start">
                    <Skeleton className="animated-skeleton" width={100} height={20} />
                    <Skeleton className="animated-skeleton" width={60} />
                  </div>
                  <div className="flex flex-col items-start">
                    <Skeleton className="animated-skeleton" width={100} height={20} />
                    <Skeleton className="animated-skeleton" width={70} />
                  </div>
                </div>
                {/* <div className="d-block gap-x-4 gap-y-2 w-full mt-2">
                <Skeleton className="animated-skeleton" width={70} height={20} />
                <div className="flex items-start space-x-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      className="animated-skeleton !rounded-[15px]"
                      width={70}
                      height={28}
                    />
                  ))}
                  <Skeleton className="animated-skeleton" circle={true} width={28} height={28} />
                </div>
              </div> */}
                {/* <div className="d-block mt-1 gap-x-4 gap-y-2 w-full">
                    <Skeleton className="animated-skeleton" width={70} height={20} />
                    <div className="flex items-start space-x-2">
                      {Array.from({ length: 2 }).map((_, index) => (
                        <Skeleton
                          key={index}
                          className="animated-skeleton"
                          circle={true}
                          width={30}
                          height={30}
                        />
                      ))}
                    </div>
                  </div> */}
                <div>
                  <Button
                    className="mt-2 w-full flex justify-center items-center"
                    icon={<IconMessageCircle size={12} />}
                    label="Loading..."
                    color="primary"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      ))}
    </div>
  )
}

export interface MostUsedAgentProps {
  agentData: AgentData[]
  isLoading?: boolean
  role?: string
  setActiveTab?: (tab: number) => void
  setAgentsData?: (data: AgentData[]) => void
  onBeforeNavigate?: () => any
}

const MostUsedAgent: React.FC<MostUsedAgentProps> = ({
  agentData,
  isLoading,
  role = 'Member',
  isPublicTeam = false,
  setActiveTab,
  setAgentsData
}) => {
  const navigate = useNavigate()
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null)
  const { teamId } = useParams<{ teamId: string }>()
  const { roles } = useSelector((state: any) => state.userContextReducer.userContext)
  const isCentraliAdmin = roles?.includes(Role.Admin)
  // The public/common team only allows Centrali admins to create agents - every other team
  // keeps the existing Owner/Contributor-based check unchanged.
  const isOwnerContributor = isPublicTeam
    ? isCentraliAdmin
    : role === 'Owner' || role === 'Contributor'
  const scrollHook = useCustomScrollbar(agentData)

  return (
    <Card className="h-100 p-2 m-0 theme-overlay-card">
      {/* Header Section */}
      <div className="row align-items-center gx-2">
        <div className="col-auto pr-0">
          <div className="rounded-lg w-20 h-20 flex items-center justify-center bg-primary text-white text-3xl font-bold d-none d-md-flex">
            <IconRobotFace size={40} color="white" />
          </div>
          <div className="rounded-lg w-12 h-12 flex items-center justify-center bg-primary text-white text-xl font-bold d-md-none">
            <IconRobotFace size={20} color="white" />
          </div>
        </div>
        <div className="col">
          <span className="d-inline-flex align-items-center">
            <p className="font-bold text-xl md:text-2xl mb-[6px] theme-text-heading">
              Most used agents
            </p>
          </span>
          <p className="subtitle-text mb-0">
            Create and manage the AI agents available to this team
          </p>
        </div>
        <div className="col-auto">
          <div className="d-flex gap-1 flex-column flex-md-row">
            {isLoading ? (
              <Skeleton
                height={36}
                width={160}
                borderRadius={6}
                className="animated-skeleton d-none d-md-block"
              />
            ) : (
              <Button
                icon={<IconEye size={14} style={{ stroke: 'var(--icon-muted)', fill: 'none' }} />}
                label={`View all - ${agentData.length ?? 0} agents`}
                color="gray"
                variant="outline"
                className="d-none d-md-inline-flex theme-neutral-outline-hover-fix"
                onClick={() => setActiveTab && setActiveTab(4)}
              />
            )}
            {isOwnerContributor && (
              <Button
                icon={<IconSquarePlus size={18} className="!text-white !stroke-white !fill-none" />}
                label="Add agent"
                color="primary"
                variant="fill"
                className="d-none d-md-inline-flex"
                onClick={() =>
                  navigate({
                    pathname: `/teams/${teamId || 1}/add-agent`
                  })
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Agent Cards */}
      {isLoading ? (
        <div className="flex">{renderAgentSkeletonLoader()}</div>
      ) : agentData?.length > 0 ? (
        <ScrollContainer scrollHook={scrollHook}>
          <div className="agent-cards-wrapper py-1">
            <AgentCards
              agentData={agentData}
              isLoading={isLoading}
              role={role}
              setAgentsData={setAgentsData}
            />
          </div>
        </ScrollContainer>
      ) : (
        <div className="flex justify-center align-center py-5">
          <EmptyContent
            icon={<IconRobotFace size={40} color="var(--color-primary)" />}
            title="No agents yet"
            description="Add an AI agent to help your team analyse data and answer questions."
            buttonOnClick={
              isOwnerContributor ? () => navigate(`/teams/${teamId}/add-agent`) : undefined
            }
            maxWidth="max-w-md"
            btnLabel={isOwnerContributor ? 'Add agent' : ''}
          />
        </div>
      )}
    </Card>
  )
}

export default MostUsedAgent
